import { animate, state, style, transition, trigger } from "@angular/animations";
import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, Pipe, PipeTransform, ViewChild } from "@angular/core";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { MatTable } from "@angular/material/table";
import { GlobalHelpDialogComponent } from "@comunicare/comunicare-dashboard-modals";
import {
  DataType,
  IDateInterval,
  IFilter,
  IInfoCommunication,
  InfoCommunication,
  ITableParameter,
  PreferenceContext,
  Reference,
} from "@comunicare/comunicare-interfaces";
import { TranslateService } from "@ngx-translate/core";
import * as moment from "moment";
import { Subject, Subscription } from "rxjs";
import { first, skipWhile, takeUntil } from "rxjs/operators";
import { Item } from "src/app/components/item-selector/item-selector.component";
import { HelpData } from "src/app/helpers/helpData";
import { CommunicationsCrudService } from "src/app/providers/communications-crud.service";
import { CommunicationsService } from "src/app/providers/communications.service";
import { HealthcareserviceService } from "src/app/providers/healthcareservice.service";
import { PreferenceService } from "src/app/providers/preference.service";
import { ResponsiveDialogService } from "src/app/providers/responsive-dialog.service";
import { SessionService } from "src/app/providers/session.service";
import { UserService } from "src/app/providers/user.service";
import { CommunicationsListDataSource } from "./communications-list-datasource";
import { SubCommunicationsListDataSource } from "./sub-communications-list-datasource";

@Pipe({ name: "isBasedOnCareplan" })
export class IsBasedOnCareplanPipe implements PipeTransform {
  transform(com: InfoCommunication): boolean {
    return com.basedOn && com.basedOn.findIndex((ref) => ref.type === "careplan") !== -1;
  }
}

@Pipe({ name: "getDataSource" })
export class GetDataSource implements PipeTransform {
  transform(
    com: InfoCommunication,
    sort: MatSort,
    dataSource: CommunicationsListDataSource,
    subDatasSources: { [key: string]: SubCommunicationsListDataSource }
  ): SubCommunicationsListDataSource {
    if (dataSource?.allDatas && com.isGrouped) {
      if (!subDatasSources[com.selfId]) {
        const allComsFromGroup = dataSource?.allDatas.filter((c) => c.selfId === com.selfId);
        const data = new SubCommunicationsListDataSource(allComsFromGroup, com.selfId);
        data.sort = sort;
        subDatasSources[com.selfId] = data;
      }
      return subDatasSources[com.selfId];
    }
  }
}

@Component({
  selector: "app-communications-page",
  templateUrl: "./communications-page.component.html",
  styleUrls: ["./communications-page.component.scss"],
  animations: [
    trigger("detailExpand", [
      state("collapsed", style({ height: "0px", minHeight: "0" })),
      state("expanded", style({ height: "*" })),
      transition("expanded <=> collapsed", animate("225ms cubic-bezier(0.4, 0.0, 0.2, 1)")),
    ]),
  ],
})
export class CommunicationsPageComponent implements OnInit, AfterViewInit, OnDestroy {
  /**
   * Material table
   */
  @ViewChild("MatPaginatorMain") paginator: MatPaginator;
  @ViewChild("MatSortMain") sort: MatSort;
  @ViewChild("MatTableMain") table: MatTable<IInfoCommunication>;

  public panelOpenState = false;
  public expandedArray: number[] = [];
  public maxFromDate: string = moment().endOf("day").format("YYYY-MM-DD");
  public minToDate: string = moment().subtract(3, "month").format("YYYY-MM-DD");
  public readonly initialFromDate: moment.Moment = moment().subtract(3, "month");
  public readonly initialToDate: moment.Moment = moment().endOf("day");
  public expandedElement: InfoCommunication | null;
  public displayedColumnsSub: string[] = ["statusSub", "receivedDateSub", "sendToSub", "actionSub"];
  public displayedColumns: string[] = [];
  /**
   * IFilter
   */
  public dataTypeText = DataType.TEXT;
  public dataTypeDate = DataType.DATE;
  public dataTypeChoice = DataType.CHOICE;

  public subDatasSources: { [key: string]: SubCommunicationsListDataSource } = {};

  public currentPageSize: number;
  public dataSource: CommunicationsListDataSource;
  /** Raw active filters of the main table, kept in sync to feed `app-item-filter` via the `filterFor` pipe. */
  public activeFilters: IFilter[] = [];
  /** Raw active filters of each sub-table (keyed by group id), kept in sync to feed `app-item-filter` via the `filterFor` pipe. */
  public subActiveFilters: { [id: string]: IFilter[] } = {};
  public availableColumnItems: Item[] = [];
  public subIsInit = false;
  public refreshSubLoading: Subscription;
  public isAllServices = true;
  /** Subject that emits when the component has been destroyed. */
  // eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle, id-denylist, id-match
  private onDestroy$ = new Subject<void>();

  constructor(
    private communicationsService: CommunicationsService,
    private translateService: TranslateService,
    private sessionService: SessionService,
    public helpData: HelpData,
    private preferenceService: PreferenceService,
    private cd: ChangeDetectorRef,
    private crud: CommunicationsCrudService,
    private responsiveDialog: ResponsiveDialogService,
    private userService: UserService,
    private depService: HealthcareserviceService
  ) {
    this.loadPreferences();
    // Define all available columns and translate display
    this.translateService
      .get(["model.patient.status", "table.sendDate", "table.receivedDate", "table.sendTo", "table.subject", "table.action"])
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((trans: string[]) => {
        this.availableColumnItems = [
          {
            value: "status",
            display: trans["model.patient.gender"],
            checked: true,
          },
          {
            value: "sendDate",
            display: trans["table.sendDate"],
            checked: true,
          },
          {
            value: "receivedDate",
            display: trans["table.receivedDate"],
            checked: true,
          },
          { value: "sendTo", display: trans["table.sendTo"], checked: true },
          { value: "subject", display: trans["table.subject"], checked: true },
          { value: "action", display: trans["table.action"], checked: true },
        ];
        this.displayedColumns = this.getDisplayedColumns();
      });
    if (
      this.sessionService.currentService &&
      this.userService.ownOrganization &&
      (this.depService.availableServices()?.length > 0 || this.depService.availableMonitoringServices().length > 0)
    ) {
      this.loadData();
      this.setupServicesWatch();
    } else {
      this.sessionService.servicesSetupWatch
        .pipe(
          skipWhile(() => !this.userService.ownOrganization),
          first()
        )
        .subscribe(() => {
          this.loadData();
          this.setupDatasource();
          this.setupServicesWatch();
        });
    }
  }

  ngOnInit(): void {
    this.displayedColumns = this.getDisplayedColumns();
    this.sessionService.refreshCommunicationsList.subscribe(() => {
      this.loadData();
      this.setupDatasource();
      this.setupServicesWatch();
    });
  }

  ngAfterViewInit(): void {
    if (this.dataSource) {
      this.setupDatasource();
    }
  }

  ngOnDestroy(): void {
    this.dataSource?.clear();
    this.refreshSubLoading?.unsubscribe();
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  private setupDatasource(): void {
    if (!this.dataSource) {
      return;
    }
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
    this.table.dataSource = this.dataSource;

    // Detect page size change
    this.paginator.page.pipe(takeUntil(this.onDestroy$)).subscribe((page) => {
      if (this.currentPageSize !== page.pageSize) {
        this.updatePreference();
      }
    });
  }

  private setupServicesWatch(): void {
    if (this.userService.isMonitoringUser) {
      this.setupServices();
      this.sessionService.refreshCurrentMonitService.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
        this.setupServices();
        this.dataSource?.loadCommunications(this.minToDate, this.maxFromDate, this.preferenceService.getAutoRefreshWidget());
        this.refreshSubLoading?.unsubscribe();
        this.refreshSubLoading = this.dataSource?.loading$.subscribe((loading) => {
          if (!loading) {
            this.subDatasSources = {};
            this.subActiveFilters = {};
          }
        });
      });
    } else {
      this.setupServices();
      this.sessionService.refreshCurrentService.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
        this.setupServices();
        this.dataSource?.loadCommunications(this.minToDate, this.maxFromDate, this.preferenceService.getAutoRefreshWidget());
        this.refreshSubLoading?.unsubscribe();
        this.refreshSubLoading = this.dataSource?.loading$.subscribe((loading) => {
          if (!loading) {
            this.subDatasSources = {};
            this.subActiveFilters = {};
          }
        });
      });
    }
  }

  private setupServices(): void {
    if (
      (!this.userService.isMonitoringUser && !this.sessionService.currentService) ||
      (this.userService.isMonitoringUser && !this.sessionService.currentMonitoringService)
    ) {
      this.isAllServices = true;
      return;
    }
    this.isAllServices = this.sessionService.isAllServices(this.userService.isMonitoringUser);
  }

  public getDisplayedColumnsSub(communication: InfoCommunication): string[] {
    return this.displayedColumnsSub.map((v) => v + communication.selfId);
  }

  private getDisplayedColumns(): string[] {
    return ["expand", ...this.availableColumnItems?.filter((item) => item.checked).map((item) => item.value)];
  }

  private loadData(): void {
    if (!this.userService.isAuthorizedSync(null, "dashboard/communications", "GET")) {
      return;
    }
    // Load data
    this.dataSource = new CommunicationsListDataSource(
      this.communicationsService,
      this.sessionService,
      this.translateService,
      this.userService,
      this.depService
    );
    this.init(this.minToDate, this.maxFromDate);
  }

  private init(from: string, to: string): void {
    this.dataSource?.loadCommunications(from, to, this.preferenceService.getAutoRefreshWidget());
    this.refreshSubLoading?.unsubscribe();
    this.refreshSubLoading = this.dataSource?.loading$.subscribe((loading) => {
      if (!loading) {
        this.subDatasSources = {};
        this.subActiveFilters = {};
      }
    });
  }

  public expand(com: InfoCommunication): void {
    this.expandedElement = this.expandedElement?.selfId === com?.selfId ? null : com;
    this.cd.detectChanges();
  }

  public isExpand(com: InfoCommunication): boolean {
    return this.expandedElement?.selfId === com.selfId;
  }

  /**
   * Drag n'Drop
   */
  public columnDropped(event: CdkDragDrop<string[]>): void {
    const realIndexFrom = this.availableColumnItems.findIndex((item) => this.displayedColumns[event.previousIndex] === item.value);
    const realIndexTo = this.availableColumnItems.findIndex((item) => this.displayedColumns[event.currentIndex] === item.value);
    moveItemInArray(this.availableColumnItems, realIndexFrom, realIndexTo);
    this.updatePreference();
  }

  /**
   * Preferences
   */
  private updatePreference(): void {
    this.preferenceService
      .update({
        context: PreferenceContext.COMMUNICATIONS_LIST_PAGE,
        parameters: {
          filters: this.dataSource?.getAllFilters(),
          availableColumnItems: this.availableColumnItems,
          itemsPerPage: this.paginator.pageSize,
        } as ITableParameter,
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe();
  }

  private loadPreferences(): void {
    this.preferenceService
      .list(PreferenceContext.COMMUNICATIONS_LIST_PAGE)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((parameters: ITableParameter) => {
        if (parameters) {
          if (parameters?.filters?.length) {
            // Apply saved filters
            parameters.filters.forEach((filter: IFilter) => {
              this.applyFilter(filter);
            });
          }
          // Set page size from preference
          this.currentPageSize = parameters.itemsPerPage;

          // Restore column order + visibility
          this.availableColumnItems = parameters.availableColumnItems;
          this.displayedColumns = this.getDisplayedColumns();
        }
      });
  }

  /**
   * IFilter
   */
  public getFilter(propertyName: string, id?: string): IFilter {
    if (!id) {
      return this.dataSource?.getFilter(propertyName);
    } else {
      return this.subDatasSources[id]?.getFilter(propertyName);
    }
  }

  public applyFilter(filter: IFilter, id?: string): void {
    if (!id) {
      this.dataSource?.setFilter(filter);
      this.activeFilters = [...(this.dataSource?.getAllFilters() ?? [])];
      this.updatePreference();
    } else {
      this.subDatasSources[id]?.setFilter(filter);
      this.subActiveFilters[id] = [...(this.subDatasSources[id]?.getAllFilters() ?? [])];
    }
  }

  public clearFilter(): void {
    this.dataSource?.clearFilter();
    this.activeFilters = [];
    this.updatePreference();
  }

  public onDateChange(interval: IDateInterval): void {
    const from = moment(interval.fromDate).format("YYYY-MM-DD");
    const to = moment(interval.toDate).format("YYYY-MM-DD");
    this.maxFromDate = moment(to).endOf("day").toISOString();
    this.minToDate = moment(from).toISOString();
    if (from && to) {
      this.dataSource?.loadCommunications(from, to, this.preferenceService.getAutoRefreshWidget());
      this.refreshSubLoading?.unsubscribe();
      this.refreshSubLoading = this.dataSource?.loading$.subscribe((loading) => {
        if (!loading) {
          this.subDatasSources = {};
          this.subActiveFilters = {};
        }
      });
    }
  }

  public viewCommunication(communication: InfoCommunication): void {
    this.crud.viewCommunication(communication);
  }

  public deleteCommunication(communication: InfoCommunication, allSimilar: boolean): void {
    let allRefs: Reference[] = [];
    if (allSimilar) {
      allRefs = this.dataSource?.allDatas.filter((v) => v.selfId === communication.selfId)?.map((v) => v.subject);
    } else {
      allRefs = [communication.subject];
    }
    this.crud.deleteCommunication(communication, allRefs);
  }

  public editCommunication(infoCom: InfoCommunication, fromMainCom?: boolean): void {
    this.crud.editCommunication(infoCom, this.dataSource?.allDatas, fromMainCom);
  }

  public createCommunication(): void {
    this.crud.createCommunication();
  }

  public openCommunicationPageHelp(): void {
    this.responsiveDialog.open(
      GlobalHelpDialogComponent,
      {
        data: { slides: this.helpData.CommunicationPageHelp },
        disableClose: true,
      },
      { maxWidth: "80vw" }
    );
  }
}
